<template>
  <div 
    class="flex flex-col h-full bg-white dark:bg-gray-900 relative transition-colors duration-300"
    @dragover.prevent="onGlobalDragOver" 
    @dragleave.prevent="onGlobalDragLeave" 
    @drop.prevent="onGlobalDrop"
  >
    
    <div v-if="isGlobalDragging && !showModal" class="drag-overlay">
      <div class="drag-content">
        <span class="drag-icon">📂</span>
        <h3>Suelta los archivos aquí</h3>
        <p>Se añadirán a la cola de subida</p>
      </div>
    </div>

    <div class="flex justify-between items-center px-6 py-4 border-b shrink-0 bg-white z-20 shadow-sm transition-colors duration-300 dark:bg-gray-800 dark:border-gray-700">
      
      <div class="flex items-center gap-4">
        <img src="/media/icon.png" alt="Logo" class="h-14 w-auto object-contain">
        <div>
          <h1 class="text-4xl font-bold text-gray-800 dark:text-white leading-tight">Gestor de Archivos</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {{ viewMode === 'table' ? 'Vista General de Base de Datos' : 'Navegación por Carpetas' }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <button @click="openUploadModalManual" class="btn-primary flex items-center gap-2 shadow-sm transition-transform hover:scale-105 active:scale-95">
          <PlusIcon :size="18" />
          <span>Nuevo Archivo</span>
        </button>

        <div class="h-8 w-px bg-gray-200 dark:bg-gray-700"></div>

        <div class="flex items-center gap-3">
            <div class="bg-gray-100 p-1 rounded-lg flex items-center gap-1 border border-gray-200 dark:bg-gray-700 dark:border-gray-600 transition-colors">
              <button @click="switchView('table')" class="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all" :class="viewMode === 'table' ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-600 dark:text-blue-300' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200'">
                <ListIcon :size="18" /> <span class="hidden sm:inline">Tabla</span>
              </button>
              <button @click="switchView('explorer')" class="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all" :class="viewMode === 'explorer' ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-600 dark:text-blue-300' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200'">
                <LayoutGridIcon :size="18" /> <span class="hidden sm:inline">Explorador</span>
              </button>
            </div>
            
            <button @click="$router.push('/settings')" class="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none" title="Configuración">
                <SettingsIcon :size="20" />
            </button>
            <button @click="toggleTheme" class="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors focus:outline-none overflow-hidden" title="Cambiar tema">
                <Transition name="rotate" mode="out-in">
                    <component :is="isDark ? SunIcon : MoonIcon" :key="isDark ? 'sun' : 'moon'" :size="20" :class="isDark ? 'text-yellow-400' : 'text-slate-600'" />
                </Transition>
            </button>
        </div>
      </div>
    </div>

    <div class="flex-1 min-h-0 relative overflow-hidden bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <KeepAlive>
        <component 
          :is="currentViewComponent" 
          ref="activeViewRef"
          @folder-change="handleFolderContext"
          class="h-full w-full" 
        />
      </KeepAlive>
    </div>

    <UploadModal 
      v-if="showModal" 
      :initial-folder="folderContext"
      :initial-file="currentFileInQueue"
      :queue-length="uploadQueue.length"
      @close="closeUploadModal"
      @uploaded="handleUploadSuccess"
      @add-to-queue="handleAddToQueue"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { LayoutGrid as LayoutGridIcon, List as ListIcon, Plus as PlusIcon, Moon as MoonIcon, Sun as SunIcon, Settings as SettingsIcon } from 'lucide-vue-next';
import FileExplorer from '@/components/FileExplorer.vue';
import FileTable from '@/components/FileTable.vue'; 
import UploadModal from '@/components/UploadModal.vue';

// --- ESTADO GENERAL ---
const viewMode = ref(localStorage.getItem('fileManagerViewMode') || 'table');
const showModal = ref(false);
const activeViewRef = ref(null);
const folderContext = ref(null);
const isDark = ref(false);

// --- ESTADO DRAG & DROP Y COLA ---
const isGlobalDragging = ref(false);
const uploadQueue = ref([]);
const currentFileInQueue = ref(null);

// --- CICLO DE VIDA ---
onMounted(() => {
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (savedTheme === 'dark' || (!savedTheme && systemPrefersDark)) {
        isDark.value = true;
        document.documentElement.classList.add('dark');
    } else {
        isDark.value = false;
        document.documentElement.classList.remove('dark');
    }
});

// --- MÉTODOS DE VISTA ---
const currentViewComponent = computed(() => viewMode.value === 'explorer' ? FileExplorer : FileTable);

const switchView = (mode) => {
  viewMode.value = mode;
  localStorage.setItem('fileManagerViewMode', mode);
  if (mode === 'table') folderContext.value = null;
};

const handleFolderContext = (folderId) => {
  if (viewMode.value === 'explorer') folderContext.value = folderId;
};

const toggleTheme = () => {
    isDark.value = !isDark.value;
    if (isDark.value) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
    } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
    }
};

// --- MÉTODOS DE DRAG & DROP GLOBAL ---
const onGlobalDragOver = (e) => {
  // Verificamos que se estén arrastrando archivos
  if (e.dataTransfer.types.includes('Files')) {
    isGlobalDragging.value = true;
  }
};

const onGlobalDragLeave = (e) => {
  // Evitar parpadeo al pasar sobre elementos hijos (como botones)
  if (e.currentTarget.contains(e.relatedTarget)) return;
  isGlobalDragging.value = false;
};

const onGlobalDrop = (e) => {
  isGlobalDragging.value = false;
  const files = Array.from(e.dataTransfer.files);
  if (files.length > 0) {
    // Siempre: el primer archivo va al modal actual, el resto a la cola
    currentFileInQueue.value = files[0];
    if (files.length > 1) {
      uploadQueue.value.push(...files.slice(1));
    }
    // Si el modal está cerrado, lo abrimos
    if (!showModal.value) {
      showModal.value = true;
    }
  }
};

// --- GESTIÓN DE LA COLA Y MODAL ---
const processQueue = () => {
  // Si el modal no está abierto y hay archivos, cogemos el primero y abrimos
  if (!showModal.value && uploadQueue.value.length > 0) {
    currentFileInQueue.value = uploadQueue.value.shift(); // Extrae el primero
    showModal.value = true;
  }
};

const openUploadModalManual = () => {
  uploadQueue.value = [];
  currentFileInQueue.value = null;
  showModal.value = true;
};

const closeUploadModal = () => {
  // Si cancela y hay cola, podrías preguntar, aquí limpiamos por seguridad
  if (uploadQueue.value.length > 0) {
    if(!confirm(`Quedan ${uploadQueue.value.length} archivos en cola. ¿Cancelar todos?`)) return;
  }
  uploadQueue.value = [];
  currentFileInQueue.value = null;
  showModal.value = false;
};

const handleAddToQueue = (files) => {
  // Añadir archivos adicionales a la cola desde el modal
  uploadQueue.value.push(...files);
};

const handleUploadSuccess = () => {
  // El modal nos avisa que terminó UNO
  showModal.value = false;
  
  // Refrescar la vista actual (Tabla o Explorador)
  if (activeViewRef.value && typeof activeViewRef.value.refresh === 'function') {
    activeViewRef.value.refresh();
  }

  // Pequeño delay para transición suave
  setTimeout(() => {
    if (uploadQueue.value.length > 0) {
      processQueue(); // Abrir el siguiente
    } else {
      currentFileInQueue.value = null;
    }
  }, 300);
};
</script>

<style scoped>
.btn-primary { @apply bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700 transition-colors; }
.rotate-enter-active, .rotate-leave-active { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
.rotate-enter-from { opacity: 0; transform: rotate(-90deg) scale(0.5); }
.rotate-leave-to { opacity: 0; transform: rotate(90deg) scale(0.5); }

.drag-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background-color: rgba(5, 96, 159, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  animation: fadeIn 0.2s ease-in;
}

.drag-content {
  text-align: center;
  color: white;
}

.drag-icon {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
  animation: bounce 1s infinite;
}

.drag-content h3 {
  font-size: 1.875rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.drag-content p {
  font-size: 1.125rem;
  opacity: 0.9;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}
</style>